import { Box, BoxProps } from '@cdl/heartbeat-react';
import { line } from 'd3-shape';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Scale } from '../../../plot/types/scale';
import { roundPixel } from '../../../utils/math';
import { getDurationFromNumberOfPoints } from '../../client/helpers';
import { togglePinnedBeat } from '../../thunks/toggle-pinned-beat.thunk';
import * as beatsActions from '../../../beats/beats.actions';

type Props = {
  data: (number | null)[];
  xScale: Scale;
  yScale: Scale;
  beatId: number;
  opacity?: 1 | 0.3;
} & BoxProps;

export const ClusterPlotStrip: React.FC<Props> = ({
  data,
  xScale,
  yScale,
  beatId,
  opacity = 0.3,
  ...props
}) => {
  const dispatch = useDispatch();
  const onSelectedCallback = useCallback(() => {
    dispatch(togglePinnedBeat(beatId));
  }, [dispatch, beatId]);

  const onHoverLeave = useCallback(() => {
    dispatch(beatsActions.toggleHoveredBeat({}));
  }, [dispatch]);

  const onHoverEnter = useCallback(() => {
    dispatch(beatsActions.toggleHoveredBeat({ id: beatId }));
  }, [dispatch, beatId]);

  const pathData = useMemo(() => {
    const newData = data
      .map((datum, i) => [datum, i])
      .filter((datum) => datum[0] != null) as number[][];
    const lineFn = line<number[]>()
      .x((n) => roundPixel(xScale(getDurationFromNumberOfPoints(n[1]))))
      .y((n) => roundPixel(yScale(n[0])));
    return lineFn(newData) || undefined;
  }, [xScale, yScale, data]);

  return (
    <Box
      as='path'
      data-testid='lead-path'
      className='lead'
      strokeWidth={4}
      opacity={opacity}
      fill='none'
      cursor='pointer'
      d={pathData}
      onMouseDown={onSelectedCallback}
      onMouseEnter={onHoverEnter}
      onMouseLeave={onHoverLeave}
      {...props}
    />
  );
};
